import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { fetchPublicSettings } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/pricing/manual", label: "Pricing" },
  { to: "/packages", label: "Packages" },
  { to: "/areas-covered", label: "Areas" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_lime-booking-hub/artifacts/xf9up4et_logo-header-only.png";

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const loc = useLocation();
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    fetchPublicSettings()
      .then(d => setLogoUrl(d?.header_logo_url || DEFAULT_LOGO))
      .catch(() => setLogoUrl(DEFAULT_LOGO));
  }, []);
  return (
    <header data-testid="site-header"
            className={`sticky top-0 z-40 backdrop-blur-xl transition-colors ${scrolled ? "bg-white/85 border-b border-black/5" : "bg-white/70"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[84px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          {logoUrl ? (
            <img src={logoUrl}
                 alt="Lime Driving School" className="h-12 w-auto animate-[fadein_300ms_ease-out]" />
          ) : (
            <div className="h-12 w-[140px]" aria-hidden="true" />
          )}
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"}
                     data-testid={`nav-${n.label.toLowerCase()}`}
                     className={({isActive}) =>
                       `text-sm tracking-tight transition-colors ${isActive ? "text-[#111827]" : "text-[#4B5563] hover:text-[#111827]"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href="tel:07472887780" className="text-sm text-[#4B5563] hover:text-[#111827] flex items-center gap-1.5" data-testid="header-phone">
            <Phone className="h-4 w-4" /> 07472 887780
          </a>
          <Link to="/packages" data-testid="header-book-btn">
            <Button className="bg-[#37B160] hover:bg-[#2D9450] text-white active:scale-95 transition-transform">
              Book lessons <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
        <button onClick={() => setOpen(v => !v)} className="md:hidden p-2 rounded-md hover:bg-black/5" data-testid="mobile-menu-toggle" aria-label="menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-black/5 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 flex flex-col gap-3">
            {NAV.map(n => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"}
                       data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                       className={({isActive}) =>
                         `text-sm py-2 ${isActive ? "text-[#111827] font-medium" : "text-[#4B5563]"}`}>
                {n.label}
              </NavLink>
            ))}
            <Link to="/packages" data-testid="mobile-book-btn">
              <Button className="w-full bg-[#37B160] hover:bg-[#2D9450] text-white">Book lessons</Button>
            </Link>
            <a href="tel:07472887780" className="text-sm text-[#4B5563] py-2">Call 07472 887780</a>
          </div>
        </div>
      )}
    </header>
  );
}
